/**
 * Derived from:
 * jQuery Text Counter Plugin v0.9.1
 * https://github.com/ractoon/jQuery-Text-Counter
 * under the MIT license.
 * 
 */

/**
 * @type {import("./public").TextCounterOptions}
 */
const defaultOptions = {
  type: "character",
  min: 0,
  max: 200,
  autoCustomAttr: "counterlimit",
  countContainerElement: "div",
  countContainerClass: "text-count-wrapper",
  textCountMessageClass: "text-count-message",
  textCountClass: "text-count",
  inputErrorClass: "error",
  counterErrorClass: "error",
  counterText: "Total Count: %d",
  errorTextElement: "div",
  minimumErrorText: "Minimum not met",
  maximumErrorText: "Maximum exceeded",
  displayErrorText: true,
  stopInputAtMaximum: true,
  countSpaces: false,
  countDown: false,
  countDownText: "Remaining: %d",
  countExtendedCharacters: false,
  twoCharCarriageReturn: false,
  countOverflow: false,
  countOverflowText: "Maximum %type exceeded by %d",
  countOverflowContainerClass: "text-count-overflow-wrapper",
  minDisplayCutoff: -1,
  maxDisplayCutoff: -1,
  maxunder: (_) => {},
  minunder: (_) => {},
  maxcount: (_) => {},
  mincount: (_) => {},
  init: (_) => {},
}

export class TextCounter {
	/** @type {Partial<import("./public").TextCounterOptions>} */
	#options = {};

	/** @type {HTMLElement|null} */
	#container = null;

	/** @type {HTMLElement|null} */
	#textCounter = null;

	/** @type {string[]} */
	static SubscribedEvents = ["keyup", "focus", "blur", "paste", "change"];

	/**
	 * @param {import("./public").HTMLValueElement} element 
	 * @param {Partial<import("./public").TextCounterOptions>} options 
	 */
	constructor(element, options) {
		/** @type {import("./public").HTMLValueElement} */
		this.element = element;

		this.#options = options;

		/** @type {import("./public").TextCounterOptions} */
		this.options = defaultOptions;

		/** @type {string} */
		this.errorText = "";

		if (this.element) {
			/** @type {any} */ (this.element).textcounter = this;
		}
	}

	init() {
		this.options = { ...defaultOptions, ...this.#options };

		const counterText = this.options.countDown ? this.options.countDownText : this.options.counterText;
		const counterNum = this.options.countDown ? this.options.max : 0;

		const formattedCounterText = document.createElement("div");
		formattedCounterText.classList.add(this.options.textCountMessageClass);
		formattedCounterText.setAttribute("aria-live", "polite");
		formattedCounterText.setAttribute("aria-atomic", "true");
		formattedCounterText.innerHTML = window.BU.policy.createHTML(counterText
			.replace("%d", `<span class="${this.options.textCountClass}">${counterNum}</span>`)
			.replace("%type", this.options.type));

		const countOverflowText = document.createElement("div");
		countOverflowText.classList.add(this.options.countOverflowContainerClass);

		this.hideMessage(countOverflowText);

		this.#container = document.createElement(this.options.countContainerElement);
		this.#container.classList.add(this.options.countContainerClass);
		this.#container.append(formattedCounterText);
		this.#container.append(countOverflowText);

		this.#textCounter = this.#container.querySelector(":scope span");
		this.element.after(this.#container);

		TextCounter.SubscribedEvents.forEach(ev => this.element.addEventListener(ev, this.checkLimits.bind(this)));

		this.options.init(this.element);

		// Readonly elements should be validated immediately, without waiting for user input.
		if ("readOnly" in this.element && this.element.readOnly) {
			this.element.dispatchEvent(new Event("change", { bubbles: true }));
		}
	}

	/**
	 * @param {Event} e 
	 */
	checkLimits(e) {
		if (!this.#container) return;
		let text = this.element.value;
		let textCount = 0;
		let textTotalCount = 0;
		const eventTriggered = "originalEvent" in e ? true : false;

		if (Object.keys(e).length !== 0) {
			textCount = this.textCount(text);
		}

		// If max is auto retrieve value.
		if (this.options.max === "auto") {
			const max = this.element.getAttribute("maxlength");
			if (max != null && max !== "false") {
				this.options.max = Number.parseInt(max);
			} else {
				this.#container.textContent = "error: [maxlength] attribute not set";
			}
		} else if (this.options.max === "autocustom") {
			const max = this.element.getAttribute(this.options.autoCustomAttr);

			if (max != null && max !== "false") {
				this.options.max = Number.parseInt(max);
			} else {
				this.#container.textContent = `error: [${this.options.autoCustomAttr}] attribute not set`;
			}
		}

		// Should be integral value by now.
		let max = /** @type {number} */ (this.options.max);

		// If this is a countdown counter, deduct from the max characters/words.
		textTotalCount = this.options.countDown ? max - textCount : textCount;

		// Set the current text count.
		this.setCount(textTotalCount);

		if (this.options.min > 0 && eventTriggered) {
			if (textCount < this.options.min) {
				this.setErrors("min");
				this.options.minunder(this.element);
			} else if (textCount >= this.options.min) {
				this.options.mincount(this.element);
				this.clearErrors("min");
			}
		}

		if (max !== -1) {
			if (textCount === max && max !== 0) {
				this.options.maxcount(this.element);
				this.clearErrors("max");
			} else if (textCount > max && max !== 0) {
				if (this.options.stopInputAtMaximum) {
					let trimmed = "";
					
					if (this.options.type === "word") {
						const words = text.split(/[^\S\n]/g);
						let i = 0;

						// Iterate over individual words
						while (i < words.length) {
							// If over the maximum words allowed, break.
							if (i >= max) break;

							if (words[i] !== "undefined") {
								trimmed += words[i] + " ";
								i++;
							}
						}
					} else { // "character"
						const maxLimit = this.options.twoCharCarriageReturn
							? max - this.twoCharCarriageReturnCount(text)
							: max;

						if (this.options.countSpaces) {
							trimmed = text.substring(0, maxLimit);
						} else {
							const charArray = text.split('');
							let totalCharacters = charArray.length;
							let charCount = 0;
							let i = 0;

							while (charCount < maxLimit && i < totalCharacters) {
								if (charArray[i] !== ' ') charCount++;
								trimmed += charArray[i++];
							}
						}
					}

					this.element.value = trimmed.trim();

					textCount = this.textCount(this.element.value);
					textTotalCount = this.options.countDown ? max - textCount : textCount;
					this.setCount(textTotalCount);
				} else {
					this.setErrors("max");
				}
			} else {
				// TextCounter: maxunder(el) Callback
				this.options.maxunder(this.element);
				this.clearErrors("max");
			}
		}

		// Hide the counter if it doesn't meet either the minimum or maximum display cutoff
		if (this.options.minDisplayCutoff == -1 && this.options.minDisplayCutoff == -1) {
			this.#container.style.display = "";
		} else if (textCount <= this.options.min + this.options.minDisplayCutoff) {
			this.#container.style.display = "";
		} else if (max !== -1 && textCount >= max - this.options.maxDisplayCutoff) {
			this.#container.style.display = "";
		} else {
			this.#container.style.display = "none";
		}
	}

	/**
	 * @param {string} text
	 * @returns {number}
	 */
	textCount(text) {
		return this.options.type === "word"
			? this.wordCount(text)
			: this.characterCount(text);
	}

	/**
	 * @param {string} text 
	 * @returns {number}
	 */
	wordCount(text) {
		return text.trim().replace(/\s+/gi, ' ').split(' ').length;
	}

	/**
	 * @param {string} text 
	 * @returns {number}
	 */
	characterCount(text) {
		let textCount = 0;
		let carriageReturnsCount = 0;

		// count carriage returns/newlines as 2 characters
		if (this.options.twoCharCarriageReturn) {
				carriageReturnsCount = this.twoCharCarriageReturnCount(text);
		}

		textCount = this.options.countSpaces
			? text.replace(/[^\S\n|\r|\r\n]/g, ' ').length
			: text.replace(/\s/g, '').length

		// count extended characters (e.g. Chinese)
		if (this.options.countExtendedCharacters) {
			const extended = text.match(/[^\x00-\xff]/gi);

			textCount = extended == null ? text.length : text.length + extended.length;
		}

		if (this.options.twoCharCarriageReturn) {
				textCount += carriageReturnsCount;
		}

		return textCount;
	}

	/**
	 * @param {string} text 
	 * @returns {number}
	 */
	twoCharCarriageReturnCount(text) {
		const carriageReturns = text.match(/(\r\n|\n|\r)/g);
		return carriageReturns?.length ?? 0;
	}

	/**
	 * @param {number} count 
	 */
	setCount(count) {
		if (!this.#textCounter) return;
		this.#textCounter.textContent = count.toString();
	}

	/**
	 * @param {"min"|"max"} type 
	 * @returns {void}
	 */
	setErrors(type) {
		let errorText = "";

		this.element.classList.add(this.options.inputErrorClass);
		if (this.#container) {
			this.#container.classList.add(this.options.counterErrorClass);
		}

		switch (type) {
			case "min":
				errorText = this.options.minimumErrorText;
				break;
			case "max":
				errorText = this.options.maximumErrorText;

				if (this.options.countOverflow) {
					this.setOverflowMessage();
				}
				break;
		}

		this.errorText = errorText;

		if (this.options.displayErrorText && this.#container) {
			const errorTextElement = this.#container.querySelector(`:scope .error-text-${type}`);
			if (errorTextElement == null) {
				this.#container.insertAdjacentHTML("afterbegin", `<${this.options.errorTextElement} class="error-text error-text-${type}">${errorText}</${this.options.errorTextElement}>`);
			}
		}
	}

	setOverflowMessage() {
		const msgElement = this.getTextCountMessageElement();
		if (msgElement == null) return;
		this.hideMessage(msgElement);

		this.removeOverflowMessage();

		// By this point, `this.options.max` is assuredly coerced to an actual integer value.
		const max = /** @type {number} */ (this.options.max);

		const overflowText = this.options.countOverflowText
				.replace('%d', (this.textCount(this.element.value) - max).toString())
				.replace('%type', this.options.type + 's');

		var overflowDiv = this.getOverflowElement();
		if (overflowDiv) {
			overflowDiv.append(overflowText);
			this.showMessage(overflowDiv);
		}
	}

	removeOverflowMessage() {
		if (this.#container == null) return;
		const overflow = this.getOverflowElement();
		if (overflow) {
			overflow.innerHTML = window.BU.createHTML("");
		}
	}

	/**
	 * @returns {HTMLElement|null}
	 */
	getOverflowElement() {
		if (this.#container == null) return null;
		return this.#container.querySelector(`:scope .${this.options.countOverflowContainerClass}`);
	}

	/**
	 * @returns {HTMLElement|null}
	 */
	getTextCountMessageElement() {
		if (this.#container == null) return null;
		return this.#container.querySelector(`:scope .${this.options.textCountMessageClass}`);
	}

	/**
	 * @param {HTMLElement} selector 
	 * @returns {void}
	 */
	showMessage(selector) {
		selector.style.display = "inline";
	}

	/**
	 * @param {HTMLElement} selector 
	 * @returns {void}
	 */
	hideMessage(selector) {
		selector.style.display = "none";
	}

	/**
	 * @param {"min"|"max"} type 
	 * @returns {void}
	 */
	clearErrors(type) {
		this.errorText = "";
		if (!this.#container) return;

		for (let child of this.#container.querySelectorAll(`:scope .error-text-${type}`)) {
			child.remove();
		}

		const errorChildren = this.#container.querySelectorAll(":scope .error-text");
		if (errorChildren.length === 0) {
			this.removeOverflowMessage();
			const msgElement = this.getTextCountMessageElement();
			if (msgElement) {
				this.showMessage(msgElement);
			}
			this.element.classList.remove(this.options.inputErrorClass);
			this.#container.classList.remove(this.options.counterErrorClass);
		}
	}
}
